<template>
  <div
    class="mobile-todo-swipe"
    :class="{
      'is-swiping': swiping,
      'is-open': offsetX < 0,
      'is-reordering': reordering,
    }"
    :data-list-id="toDoListId"
    @touchstart.passive="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
    @pointerdown="onPointerDown"
  >
    <button
      v-show="swiping || offsetX < 0"
      type="button"
      class="mobile-todo-delete-action"
      :tabindex="offsetX < 0 ? 0 : -1"
      @click.stop="removeTodo"
    >
      {{ $t("ui.remove") }}
    </button>

    <div
      class="mobile-todo-item"
      :class="{
        'is-checked': toDo.checked,
        'is-editing': editing,
        'has-inline-details': showInlineDetails && hasInlineDetails,
      }"
      :style="itemStyle"
    >
      <div class="mobile-todo-item-main">
        <button
          type="button"
          class="mobile-todo-check"
          :aria-pressed="toDo.checked"
          :aria-label="toDo.checked ? 'Uncheck' : 'Check'"
          @click.stop="toggleCheck"
        >
          <span
            v-if="toDo.color != 'none'"
            class="cicle-icon"
            :style="'color: ' + toDo.color"
            :class="{
              'bi-check-circle-fill': toDo.checked,
              'bi-circle-fill': !toDo.checked,
            }"
          ></span>
          <span
            v-else
            class="cicle-icon"
            :class="{
              'bi-check-circle': toDo.checked,
              'bi-circle': !toDo.checked,
            }"
          ></span>
        </button>

        <div v-if="editing" class="mobile-todo-body is-editing">
          <input
            ref="titleInput"
            class="mobile-todo-title-input"
            type="text"
            v-model="draftText"
            @keydown.enter="onEnter"
            @keydown.esc.prevent="cancelEdit"
            @blur="doneEdit"
          />
          <span v-if="toDo.time" class="mobile-todo-time" :class="{ 'checked-todo': toDo.checked }">
            {{ timeFormat(toDo.time) }}
            <span
              class="alarm-indicator"
              :class="{ 'show-alarm-indicator': notificationIndicator && toDo.alarm }"
            ></span>
          </span>
        </div>
        <button v-else type="button" class="mobile-todo-body" @click="onBodyClick">
          <span class="mobile-todo-text" :class="{ 'checked-todo': toDo.checked }" v-html="todoText"></span>
          <span v-if="toDo.time" class="mobile-todo-time" :class="{ 'checked-todo': toDo.checked }">
            {{ timeFormat(toDo.time) }}
            <span
              class="alarm-indicator"
              :class="{ 'show-alarm-indicator': notificationIndicator && toDo.alarm }"
            ></span>
          </span>
        </button>

        <!-- Calendar days: more menu (postpone + details). Custom lists: chevron → details. -->
        <div v-if="isCalendarDay" class="mobile-todo-more">
          <button
            ref="moreToggle"
            type="button"
            class="mobile-todo-details"
            :aria-label="$t('ui.more')"
            :aria-expanded="moreMenuOpen ? 'true' : 'false'"
            @mousedown.prevent
            @click.stop="toggleMoreMenu"
          >
            <i class="bi-three-dots-vertical" aria-hidden="true"></i>
          </button>
          <Teleport to="#app-container">
            <ul
              v-if="moreMenuOpen"
              ref="morePopover"
              class="dropdown-menu show mobile-todo-more-menu"
              :style="moreMenuStyle"
              @click.stop
            >
              <li v-if="!toDo.checked">
                <button class="dropdown-item" type="button" @click="postponeOneDay">
                  <i class="bi-reply" aria-hidden="true"></i>
                  <span>{{ $t("ui.postpone") }}</span>
                </button>
              </li>
              <li>
                <button class="dropdown-item" type="button" @click="openDetails">
                  <i class="bi-info-circle" aria-hidden="true"></i>
                  <span>{{ $t("todoDetails.details") }}</span>
                </button>
              </li>
            </ul>
          </Teleport>
        </div>
        <button
          v-else
          type="button"
          class="mobile-todo-details"
          aria-label="Details"
          @mousedown.prevent
          @click.stop="openDetails"
        >
          <i class="bi-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <div
        v-if="showInlineDetails && hasInlineDetails"
        class="mobile-todo-inline-details"
        @click.stop
      >
        <p v-if="inlineDesc" class="mobile-todo-inline-desc">{{ inlineDesc }}</p>
        <ul v-if="inlineSubTasks.length" class="mobile-todo-inline-subtasks">
          <li
            v-for="row in inlineSubTasks"
            :key="row.index"
            class="mobile-todo-inline-subtask"
            :class="{ checked: row.st.checked }"
          >
            <button
              type="button"
              class="mobile-todo-inline-subtask-check"
              :aria-pressed="!!row.st.checked"
              :aria-label="row.st.checked ? 'Uncheck' : 'Check'"
              @click.stop="toggleInlineSubTask(row.index)"
            >
              <i
                class="cicle-icon"
                :class="row.st.checked ? 'bi-check-circle' : 'bi-circle'"
                aria-hidden="true"
              ></i>
              <span>{{ row.st.text }}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import toDoListRepository from "../repositories/toDoListRepository";
import { Modal, Toast } from "bootstrap";
import moment from "moment";
import notifications from "../helpers/notifications";
import linkifyStr from "linkify-string";
import tasksHelper from "../helpers/tasksHelper";

const DELETE_WIDTH = 80;
const OPEN_THRESHOLD = 40;
// 纵向滚动优先：位移超过此值才定轴；横向需明显大于纵向才锁 x，避免斜滑闪出删除
const AXIS_LOCK = 12;
const HORIZONTAL_RATIO = 1.35;
const LONG_PRESS_MS = 400;
const CLOSE_EVENT = "mobile-todo-swipe-close";

function afterTodoWrite(promise, fn) {
  Promise.resolve(promise).then((result) => {
    let ok = false;
    try {
      ok = require("../helpers/todoActivityLog.js").isTodoWriteOk(result);
    } catch (_) {
      ok = false;
    }
    if (!ok) return;
    fn();
  }, () => {});
}

function afterTodoDelete(promise, fn) {
  Promise.resolve(promise).then((result) => {
    let ok = false;
    try {
      ok = require("../helpers/todoActivityLog.js").isTodoDeleteWriteOk(result);
    } catch (_) {
      ok = false;
    }
    if (!ok) return;
    fn();
  }, () => {});
}

function beginUndoNotesCascade(todoId) {
  try {
    return require("../helpers/todoActivityLog.js").planUndoableNotesCascade(todoId);
  } catch (_) {
    return { commit() {} };
  }
}

function showTaskRemovedToast() {
  const toastEl = document.getElementById("taskRemoved");
  if (!toastEl) return 0;
  const toast = new Toast(toastEl);
  const configured = toast._config && toast._config.delay;
  toast.show();
  if (typeof configured === "number" && configured >= 0) return configured;
  try {
    return require("../helpers/todoActivityLog.js").UNDO_TOAST_MS;
  } catch (_) {
    return 5000;
  }
}

function commitUndoNotesCascade(plan, delayMs, readList) {
  try {
    const { deleteNotesByTodoId } = require("../sync/engine");
    const { shouldCascadeTodoNotes } = require("../helpers/todoActivityLog.js");
    plan.commit({
      deleteFn: (todoId) => {
        const list = typeof readList === "function" ? readList() : readList;
        if (!shouldCascadeTodoNotes(list, todoId)) return;
        return deleteNotesByTodoId(todoId);
      },
      delayMs,
    });
  } catch (_) {
    /* cascade must not block the todo delete */
  }
}

function logActivity(vm, type, todo, listId, meta) {
  try {
    const { logTodoActivity } = require("../helpers/todoActivityLog.js");
    const { createNote } = require("../sync/engine");
    const pending = logTodoActivity({
      type,
      todo,
      listId,
      meta,
      t: vm.$t.bind(vm),
      createNoteFn: createNote,
    });
    if (pending && typeof pending.then === "function") pending.then(() => {}, () => {});
  } catch (err) {
    /* activity log must not block the todo write */
  }
}

export default {
  name: "MobileToDoItem",
  props: {
    toDo: { required: true, type: Object },
    index: { required: true, type: Number },
    toDoListId: { required: true, type: String },
    showInlineDetails: { type: Boolean, default: false },
  },
  data() {
    return {
      options: { target: "_blank", defaultProtocol: "https" },
      editing: false,
      draftText: "",
      offsetX: 0,
      swiping: false,
      touchStartX: 0,
      touchStartY: 0,
      baseOffsetX: 0,
      axis: null,
      suppressClick: false,
      reordering: false,
      dragLiftY: 0,
      dragFromIndex: -1,
      dragTodo: null,
      insertHintIndex: -1,
      longPressTimer: null,
      mouseDragActive: false,
      moreMenuOpen: false,
      moreMenuStyle: {},
    };
  },
  computed: {
    todoText() {
      return linkifyStr(this.toDo.text, this.options);
    },
    notificationIndicator() {
      return this.$store.getters.config.notificationIndicator;
    },
    itemStyle() {
      if (this.reordering) {
        return {
          transform: "translateY(" + this.dragLiftY + "px)",
          transition: "none",
          zIndex: 5,
        };
      }
      // Avoid transform at rest — it makes position:fixed menus clip under overflow:hidden
      if (!this.swiping && this.offsetX === 0) return {};
      const style = { transform: "translateX(" + this.offsetX + "px)" };
      if (this.swiping || this.offsetX < 0) {
        style.willChange = "transform";
      }
      return style;
    },
    isCalendarDay() {
      return /^\d{8}$/.test(String(this.toDoListId || ""));
    },
    inlineDesc() {
      const d = this.toDo && this.toDo.desc;
      return typeof d === "string" ? d.trim() : "";
    },
    inlineSubTasks() {
      const list = this.toDo && this.toDo.subTaskList;
      if (!Array.isArray(list)) return [];
      return list
        .map((st, index) => ({ st, index }))
        .filter((row) => row.st && String(row.st.text || "").trim());
    },
    hasInlineDetails() {
      return !!this.inlineDesc || this.inlineSubTasks.length > 0;
    },
    moveSubtaskToBottom() {
      return this.$store.getters.config.moveCompletedSubTaskToBottom !== false;
    },
  },
  mounted() {
    this.onCloseOthers = (event) => {
      if (event.detail && event.detail.source === this) return;
      this.closeSwipe();
      this.closeMoreMenu();
    };
    document.addEventListener(CLOSE_EVENT, this.onCloseOthers);
  },
  beforeUnmount() {
    this.clearLongPress();
    this.clearDropHints();
    this.unbindMouseDrag();
    this.closeMoreMenu();
    document.removeEventListener(CLOSE_EVENT, this.onCloseOthers);
  },
  methods: {
    resolveAxis(dx, dy) {
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      if (ax < AXIS_LOCK && ay < AXIS_LOCK) return null;
      // 斜向滚动时优先给纵向（列表滚动），横向需明显更大才进入删除手势
      if (ax > ay * HORIZONTAL_RATIO) return "x";
      return "y";
    },
    clearLongPress() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    isDragIgnoreTarget(target) {
      if (!target || !target.closest) return false;
      return !!(
        target.closest(".mobile-todo-check") ||
        target.closest(".mobile-todo-details") ||
        target.closest(".mobile-todo-more") ||
        target.closest(".mobile-todo-delete-action") ||
        target.closest(".mobile-todo-title-input") ||
        target.closest(".mobile-todo-inline-details")
      );
    },
    bindMouseDrag() {
      if (this.mouseDragActive) return;
      this.mouseDragActive = true;
      this._onPointerMove = (e) => this.onPointerMove(e);
      this._onPointerUp = (e) => this.onPointerUp(e);
      window.addEventListener("pointermove", this._onPointerMove);
      window.addEventListener("pointerup", this._onPointerUp);
      window.addEventListener("pointercancel", this._onPointerUp);
    },
    unbindMouseDrag() {
      if (!this.mouseDragActive) return;
      this.mouseDragActive = false;
      window.removeEventListener("pointermove", this._onPointerMove);
      window.removeEventListener("pointerup", this._onPointerUp);
      window.removeEventListener("pointercancel", this._onPointerUp);
      this._onPointerMove = null;
      this._onPointerUp = null;
    },
    finishReorderGesture() {
      const toIndex = this.insertHintIndex;
      const fromIndex = this.dragFromIndex;
      const todo = this.dragTodo;
      this.clearDropHints();
      this.reordering = false;
      this.dragLiftY = 0;
      this.axis = null;
      this.dragTodo = null;
      this.dragFromIndex = -1;
      this.insertHintIndex = -1;
      if (todo && fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
        this.commitReorder(todo, fromIndex, toIndex);
      }
    },
    onPointerDown(event) {
      // 触控仍走 touch*；此处仅 PC 鼠标/笔
      if (event.pointerType === "touch") return;
      if (event.button !== 0) return;
      if (this.editing || this.isDragIgnoreTarget(event.target)) return;

      this.notifyCloseOthers();
      this.touchStartX = event.clientX;
      this.touchStartY = event.clientY;
      this.baseOffsetX = this.offsetX;
      this.axis = null;
      this.swiping = false;
      this.suppressClick = false;
      this.clearLongPress();
      if (this.offsetX < 0) {
        this.closeSwipe();
        return;
      }
      this.bindMouseDrag();
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        this.beginReorder();
      }, LONG_PRESS_MS);
    },
    onPointerMove(event) {
      if (event.pointerType === "touch") return;
      const dx = event.clientX - this.touchStartX;
      const dy = event.clientY - this.touchStartY;

      if (this.reordering) {
        if (event.cancelable) event.preventDefault();
        this.dragLiftY = dy;
        const hint = this.computeTargetIndex(event.clientY);
        this.insertHintIndex = hint;
        this.updateDropHint(hint);
        return;
      }

      if (!this.axis) {
        this.axis = this.resolveAxis(dx, dy);
        if (!this.axis) return;
        this.clearLongPress();
        // PC：纵向拖一下即可进入排序（不必等满长按）
        if (this.axis === "y") {
          this.beginReorder();
          this.dragLiftY = dy;
          const hint = this.computeTargetIndex(event.clientY);
          this.insertHintIndex = hint;
          this.updateDropHint(hint);
          return;
        }
      }

      // PC：横向拖动露出删除（与触控左滑一致）
      if (this.axis === "x") {
        this.swiping = true;
        this.offsetX = Math.max(-DELETE_WIDTH, Math.min(0, this.baseOffsetX + dx));
      }
    },
    onPointerUp(event) {
      if (event.pointerType === "touch") return;
      this.clearLongPress();
      this.unbindMouseDrag();
      if (this.reordering) {
        this.finishReorderGesture();
        return;
      }
      if (this.axis === "x") {
        this.suppressClick = Math.abs(this.offsetX - this.baseOffsetX) > 4;
        this.offsetX = this.offsetX <= -OPEN_THRESHOLD ? -DELETE_WIDTH : 0;
      }
      this.axis = null;
      this.swiping = false;
    },
    closeSwipe() {
      this.offsetX = 0;
      this.swiping = false;
      if (!this.reordering) this.axis = null;
      this.closeMoreMenu();
    },
    notifyCloseOthers() {
      document.dispatchEvent(
        new CustomEvent(CLOSE_EVENT, { detail: { source: this } })
      );
    },
    clearDropHints() {
      const list = this.$el && this.$el.closest(".to-do-list");
      if (!list) return;
      list.querySelectorAll(".mobile-todo-swipe.is-drop-target").forEach((el) => {
        el.classList.remove("is-drop-target");
      });
    },
    updateDropHint(toIndex) {
      this.clearDropHints();
      const list = this.$el && this.$el.closest(".to-do-list");
      if (!list) return;
      const rows = list.querySelectorAll(":scope > li .mobile-todo-swipe");
      const el = rows[toIndex];
      if (el && el !== this.$el) el.classList.add("is-drop-target");
    },
    listRowElements() {
      const list = this.$el && this.$el.closest(".to-do-list");
      if (!list) return [];
      return Array.from(list.querySelectorAll(":scope > li .mobile-todo-swipe"));
    },
    computeTargetIndex(clientY) {
      const rows = this.listRowElements();
      if (!rows.length) return this.dragFromIndex;
      for (let i = 0; i < rows.length; i++) {
        const rect = rows[i].getBoundingClientRect();
        if (clientY < rect.top + rect.height / 2) return i;
      }
      return rows.length - 1;
    },
    beginReorder() {
      if (this.editing || this.offsetX < 0 || this.reordering) return;
      this.clearLongPress();
      this.closeSwipe();
      this.notifyCloseOthers();
      this.reordering = true;
      this.axis = "reorder";
      this.swiping = false;
      this.dragFromIndex = this.index;
      this.dragTodo = this.toDo;
      this.dragLiftY = 0;
      this.insertHintIndex = this.index;
      this.suppressClick = true;
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate(10);
        } catch (e) {
          /* ignore */
        }
      }
    },
    onTouchStart(event) {
      if (this.editing) return;
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      this.notifyCloseOthers();
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.baseOffsetX = this.offsetX;
      this.axis = null;
      this.swiping = false;
      this.suppressClick = false;
      this.clearLongPress();
      if (this.offsetX < 0) return;
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        this.beginReorder();
      }, LONG_PRESS_MS);
    },
    onTouchMove(event) {
      if (this.editing) return;
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;

      if (this.reordering) {
        event.stopPropagation();
        if (event.cancelable) event.preventDefault();
        this.dragLiftY = dy;
        const hint = this.computeTargetIndex(touch.clientY);
        this.insertHintIndex = hint;
        this.updateDropHint(hint);
        return;
      }

      if (!this.axis) {
        this.axis = this.resolveAxis(dx, dy);
        if (!this.axis) return;
        this.clearLongPress();
        // 已判定为纵向滚动：立刻放弃左滑，避免红按钮闪一下再收回
        if (this.axis === "y") {
          this.offsetX = 0;
          this.swiping = false;
          return;
        }
      }
      if (this.axis !== "x") return;

      event.stopPropagation();
      if (event.cancelable) event.preventDefault();
      this.swiping = true;
      const next = this.baseOffsetX + dx;
      this.offsetX = Math.max(-DELETE_WIDTH, Math.min(0, next));
    },
    onTouchEnd(event) {
      if (this.editing) return;
      this.clearLongPress();

      if (this.reordering) {
        event.stopPropagation();
        this.finishReorderGesture();
        return;
      }

      if (this.axis === "x") {
        event.stopPropagation();
        this.suppressClick = Math.abs(this.offsetX - this.baseOffsetX) > 4;
        this.offsetX = this.offsetX <= -OPEN_THRESHOLD ? -DELETE_WIDTH : 0;
      }
      this.swiping = false;
      this.axis = null;
    },
    commitReorder(todo, fromIndex, toIndex) {
      const toDoListId = this.toDoListId;
      const list = this.$store.getters.todoLists[toDoListId];
      if (!list) return;
      const currentIndex = list.indexOf(todo);
      const from = currentIndex >= 0 ? currentIndex : fromIndex;
      if (from < 0 || from >= list.length) return;
      let to = Math.max(0, Math.min(toIndex, list.length - 1));
      if (from === to) return;

      this.$store.commit("removeTodo", { toDoListId, index: from });
      this.$store.commit("insertTodo", {
        toDoListId,
        index: to,
        toDo: todo,
      });

      const ordered = this.$store.getters.todoLists[toDoListId];
      tasksHelper.applyListOrder(ordered, this.$store.getters.config);
      toDoListRepository.update(toDoListId, ordered);
      notifications.refreshDayNotifications(this, toDoListId);
    },
    onBodyClick(e) {
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      if (this.offsetX < 0) {
        this.closeSwipe();
        return;
      }
      this.startEdit(e);
    },
    toggleCheck() {
      if (this.offsetX < 0) {
        this.closeSwipe();
        return;
      }
      if (this.editing) this.doneEdit();
      const toDoListId = this.toDoListId;
      const index = this.index;
      this.$store.commit("checkTodo", { toDoListId, index });
      const todo = this.$store.getters.todoLists[toDoListId][index];
      if (todo && todo.checked && Array.isArray(todo.subTaskList) && todo.subTaskList.length) {
        todo.subTaskList.forEach((st) => {
          if (st) st.checked = true;
        });
      }
      const activityType = todo && todo.checked ? "completed" : "uncompleted";
      const activityTodo = todo ? { id: todo.id, text: todo.text } : null;
      const written = this.applyCheckSideEffects(toDoListId);
      afterTodoWrite(written, () => {
        logActivity(this, activityType, activityTodo, toDoListId);
      });
    },
    toggleInlineSubTask(subIndex) {
      if (this.offsetX < 0) {
        this.closeSwipe();
        return;
      }
      const toDoListId = this.toDoListId;
      const list = this.$store.getters.todoLists[toDoListId];
      const todo = list && list[this.index];
      if (!todo || !Array.isArray(todo.subTaskList) || !todo.subTaskList[subIndex]) return;
      const st = todo.subTaskList[subIndex];
      st.checked = !st.checked;
      if (st.checked && this.moveSubtaskToBottom) {
        todo.subTaskList.push(todo.subTaskList.splice(subIndex, 1)[0]);
      }
      const { touchTodo } = require("../sync/todoMerge.js");
      touchTodo(todo);
      const activityTodo = { id: todo.id, text: todo.text };
      const checked = !!st.checked;
      const subtaskText = st.text;
      const written = toDoListRepository.update(toDoListId, list);
      afterTodoWrite(written, () => {
        const untitled = this.$t("activity.untitled");
        logActivity(
          this,
          checked ? "subtask_completed" : "subtask_uncompleted",
          activityTodo,
          toDoListId,
          { subtask: (subtaskText && String(subtaskText).trim()) || untitled }
        );
      });
    },
    applyCheckSideEffects(toDoListId) {
      const list = this.$store.getters.todoLists[toDoListId];
      tasksHelper.applyListOrder(list, this.$store.getters.config);
      const written = toDoListRepository.update(toDoListId, list);
      notifications.refreshDayNotifications(this, toDoListId);
      return written;
    },
    startEdit(e) {
      if (e.target.href) return;
      this.draftText = this.toDo.text || "";
      this.editing = true;
      this.$nextTick(() => {
        const input = this.$refs.titleInput;
        if (!input) return;
        input.focus();
        input.select();
        // 键盘弹出后把当前行滚进可视区（列表吸底输入也依赖同一套 focusin）
        try {
          input.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
        } catch (err) {
          /* ignore */
        }
      });
    },
    onEnter(e) {
      if (e.isComposing || e.keyCode === 229) return;
      e.preventDefault();
      this.doneEdit();
    },
    doneEdit() {
      if (!this.editing) return;
      const text = this.draftText;
      this.editing = false;
      if (text === this.toDo.text) return;
      const from = this.toDo.text;
      const to = text;
      this.$store.commit("updateTodo", {
        toDoListId: this.toDoListId,
        index: this.index,
        text,
      });
      const written = toDoListRepository.update(
        this.toDoListId,
        this.$store.getters.todoLists[this.toDoListId]
      );
      notifications.refreshDayNotifications(this, this.toDoListId);
      const untitled = this.$t("activity.untitled");
      afterTodoWrite(written, () => {
        logActivity(
          this,
          "title_changed",
          { id: this.toDo.id, text: to },
          this.toDoListId,
          {
            from: (from && String(from).trim()) || untitled,
            to: (to && String(to).trim()) || untitled,
          }
        );
      });
    },
    cancelEdit() {
      if (!this.editing) return;
      this.editing = false;
      this.draftText = this.toDo.text || "";
    },
    toggleMoreMenu() {
      if (this.moreMenuOpen) {
        this.closeMoreMenu();
        return;
      }
      if (this.offsetX < 0) this.closeSwipe();
      this.notifyCloseOthers();
      this.openMoreMenu();
    },
    openMoreMenu() {
      const btn = this.$refs.moreToggle;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const menuWidth = 160;
      let left = rect.right - menuWidth;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - menuWidth - 8);
      }
      this.moreMenuStyle = {
        position: "fixed",
        top: `${Math.round(rect.bottom + 4)}px`,
        left: `${Math.round(left)}px`,
        width: `${menuWidth}px`,
        zIndex: 2100,
      };
      this.moreMenuOpen = true;
      this.$nextTick(() => {
        setTimeout(() => {
          document.addEventListener("pointerdown", this.onDocCloseMore, true);
        }, 0);
      });
    },
    closeMoreMenu() {
      this.moreMenuOpen = false;
      document.removeEventListener("pointerdown", this.onDocCloseMore, true);
    },
    onDocCloseMore(event) {
      const btn = this.$refs.moreToggle;
      const pop = this.$refs.morePopover;
      if (btn && btn.contains(event.target)) return;
      if (pop && pop.contains(event.target)) return;
      this.closeMoreMenu();
    },
    postponeOneDay() {
      if (!this.isCalendarDay || !this.toDo || this.toDo.checked) return;
      if (this.offsetX < 0) this.closeSwipe();
      if (this.editing) this.doneEdit();
      this.closeMoreMenu();

      const fromId = String(this.toDoListId);
      const toId = moment(fromId, "YYYYMMDD").add(1, "d").format("YYYYMMDD");
      const toLabel = moment(fromId, "YYYYMMDD").add(1, "d").format("YYYY-MM-DD");
      const todo = this.toDo;
      const index = this.index;
      const reId = todo.repeatingEvent;
      const activityTodo = { id: todo.id, text: todo.text };

      if (reId) {
        const repeatingEventHelper = require("../helpers/repeatingEvents.js").default;
        repeatingEventHelper.markDayHandled(this, fromId, reId);
      }

      if (!Array.isArray(this.$store.getters.todoLists[toId])) {
        this.$store.commit("loadTodoLists", { todoListId: toId, todoList: [] });
      }

      this.$store.commit("removeTodo", { toDoListId: fromId, index });
      todo.listId = toId;
      todo.repeatingEvent = null;
      const { touchTodo } = require("../sync/todoMerge.js");
      touchTodo(todo);
      this.$store.commit("addTodo", todo);

      const fromList = this.$store.getters.todoLists[fromId];
      const toList = this.$store.getters.todoLists[toId];
      tasksHelper.applyListOrder(toList, this.$store.getters.config);
      const originWrite = toDoListRepository.update(fromId, fromList);
      const destWrite = toDoListRepository.update(toId, toList);
      notifications.refreshDayNotifications(this, fromId);
      notifications.refreshDayNotifications(this, toId);
      afterTodoWrite(Promise.all([originWrite, destWrite]), () => {
        logActivity(this, "postponed", activityTodo, toId, { toLabel });
      });
    },
    openDetails() {
      if (this.offsetX < 0) {
        this.closeSwipe();
        return;
      }
      if (this.editing) this.doneEdit();
      this.closeMoreMenu();

      this.$store.commit("actionsSelectedTodoIdUpdate", {
        toDo: this.toDo,
        index: this.index,
      });

      const { openTodoDetail } = require("./mobileNav.js");
      if (
        openTodoDetail({
          listId: this.toDoListId,
          index: this.index,
        })
      ) {
        return;
      }

      // Defer so the opening click cannot fall through onto modal header dropdowns.
      const modalEl = document.getElementById("toDoModal");
      const modal = Modal.getOrCreateInstance(modalEl, { keyboard: false });
      setTimeout(() => modal.show(), 0);
    },
    removeTodo() {
      const todo = this.toDo;
      const index = this.index;
      const toDoListId = this.toDoListId;
      const todoId = todo && todo.id;
      this.closeSwipe();
      if (todo && todo.repeatingEvent) {
        const repeatingEventHelper = require("../helpers/repeatingEvents.js").default;
        repeatingEventHelper.markDayHandled(this, toDoListId, todo.repeatingEvent);
      }
      this.$store.commit("setUndoElement", { type: "task", todo, index });
      this.$store.commit("removeTodo", { toDoListId, index });
      const written = toDoListRepository.update(toDoListId, this.$store.getters.todoLists[toDoListId]);
      notifications.refreshDayNotifications(this, toDoListId);
      const notesCascade = beginUndoNotesCascade(todoId);
      const undoDelay = showTaskRemovedToast();
      afterTodoDelete(written, () =>
        commitUndoNotesCascade(notesCascade, undoDelay, () => this.$store.getters.todoLists[toDoListId])
      );
    },
    timeFormat(date) {
      if (date) {
        return moment(date, "HH:mm").format("hh:mm a");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.mobile-todo-swipe {
  position: relative;
  overflow: hidden;
  background: var(--m-surface, #fff);
  cursor: grab;
  touch-action: pan-y;
}

.mobile-todo-swipe.is-reordering {
  overflow: visible;
  z-index: 4;
  cursor: grabbing;
  user-select: none;
}

.mobile-todo-swipe.is-reordering * {
  user-select: none;
}

.mobile-todo-swipe.is-reordering .mobile-todo-delete-action {
  display: none;
}

.mobile-todo-swipe.is-drop-target::before {
  content: "";
  position: absolute;
  left: 12px;
  right: 12px;
  top: 0;
  height: 2px;
  background: #007aff;
  z-index: 3;
  pointer-events: none;
}

.dark-theme .mobile-todo-swipe.is-drop-target::before {
  background: #0a84ff;
}

.mobile-todo-delete-action {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80px;
  margin: 0;
  padding: 0;
  border: none;
  background: #ff3b30;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
}

.dark-theme .mobile-todo-delete-action {
  background: #ff453a;
}

.mobile-todo-item {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: var(--m-row-min, 48px);
  border-bottom: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
  background: var(--m-surface, #fff);
  padding: 0;
  transition: transform 0.18s ease-out;
}

.mobile-todo-item-main {
  display: flex;
  align-items: flex-start;
  min-height: var(--m-row-min, 48px);
  width: 100%;
}

.mobile-todo-inline-details {
  padding: 0 12px 10px 52px;
  color: var(--m-secondary-label, rgba(60, 60, 67, 0.6));
  font-size: 13px;
  line-height: 1.35;
}

.mobile-todo-inline-desc {
  margin: 0 0 6px;
  white-space: pre-wrap;
  word-break: break-word;
}

.mobile-todo-inline-subtasks {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-todo-inline-subtask {
  padding: 0;
}

.mobile-todo-inline-subtask-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 4px 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.mobile-todo-inline-subtask-check .cicle-icon {
  flex: 0 0 auto;
  font-size: 16px;
  line-height: 1.2;
  opacity: 0.75;
  margin-top: 1px;
}

.mobile-todo-inline-subtask.checked .mobile-todo-inline-subtask-check span {
  text-decoration: line-through;
  opacity: 0.55;
}

.dark-theme .mobile-todo-inline-details {
  color: var(--m-secondary-label, rgba(235, 235, 245, 0.55));
}

.mobile-todo-more {
  flex: 0 0 auto;
  align-self: stretch;
  display: flex;
}

.mobile-todo-swipe.is-swiping .mobile-todo-item,
.mobile-todo-swipe.is-reordering .mobile-todo-item {
  transition: none;
}

.mobile-todo-swipe.is-reordering .mobile-todo-item {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  opacity: 0.92;
  border-radius: 8px;
}

.dark-theme .mobile-todo-swipe.is-reordering .mobile-todo-item {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
}

.mobile-todo-check {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--m-tap, 44px);
  min-height: var(--m-tap, 44px);
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}

.cicle-icon {
  font-size: 22px;
  line-height: 1;
}

.bi-check-circle-fill,
.bi-check-circle {
  opacity: 0.7;
}

.mobile-todo-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  min-height: var(--m-row-min, 48px);
  margin: 0;
  padding: 10px 8px 10px 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}

.mobile-todo-body.is-editing {
  padding-top: 6px;
  padding-bottom: 6px;
}

.mobile-todo-title-input {
  width: 100%;
  margin: 0;
  padding: 4px 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: inherit;
  font-size: var(--m-text, 17px);
  line-height: var(--m-line, 1.35);
  letter-spacing: -0.01em;
  outline: none;
  box-shadow: none;
}

.mobile-todo-text {
  display: -webkit-box;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 0;
  font-size: var(--m-text, 17px);
  line-height: var(--m-line, 1.35);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  overflow-wrap: anywhere;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  /* autoprefixer 会误删这一行，导致多行省略失效 */
  /* autoprefixer: ignore next */
  -webkit-box-orient: vertical;
  max-height: calc(var(--m-line, 1.35) * 3em);
}

.mobile-todo-time {
  margin-top: 2px;
  font-size: var(--m-caption, 13px);
  color: var(--m-muted, rgba(60, 60, 67, 0.6));
  line-height: 1.3;
}

.mobile-todo-details {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-width: var(--m-tap, 44px);
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--m-muted, rgba(60, 60, 67, 0.45));
  font-size: 18px;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
}

.mobile-todo-details:hover,
.mobile-todo-details:focus-visible {
  color: var(--m-fg, rgba(60, 60, 67, 0.85));
}

.mobile-todo-more-menu {
  min-width: 140px;
  padding: 6px 0;
  border-radius: 12px;
  border: none;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
}

.mobile-todo-more-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  font-size: 15px;
}

.mobile-todo-more-menu .dropdown-item i {
  font-size: 16px;
  opacity: 0.7;
}

.dark-theme .mobile-todo-more-menu {
  background: #2c2c2e;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.45);
}

.dark-theme .mobile-todo-more-menu .dropdown-item {
  color: #f2f2f7;
}

.dark-theme .mobile-todo-more-menu .dropdown-item:hover,
.dark-theme .mobile-todo-more-menu .dropdown-item:focus {
  background: rgba(255, 255, 255, 0.08);
}

.checked-todo {
  text-decoration: line-through;
  opacity: 0.45;
  color: var(--m-muted, rgba(60, 60, 67, 0.6));
}

.mobile-todo-item.is-checked .cicle-icon {
  opacity: 0.55;
}
</style>
